import {
  ClickEventObject,
  PageViewEventObject,
  GlobalEventObject,
  TrackingPayload,
  ApplicationIdentifier,
  Plugin,
  GlobalData,
  PageData,
  TrackingInstance,
  SpaEvent,
  EventDetailPayload,
  TrackingDetailObject,
} from './Tracking';
import { EventType, IntegrationType } from './Tracking.enums';
import { TRACKING_ATTRIBUTES, TRACKING_DATA_ATTRIBUTES, VALUES } from './Tracking.config';
import { MediaQueryService } from '@owc-utils/MediaQueryService';

// exported tracking string formater to be used in other components
// OWCA-579 return undefined instead of empty string
export const formatTrackingString = function(trackingString: string | null): string | undefined {
  if (trackingString !== null) {
    const trimmedLabel = trackingString.trim();
    return trimmedLabel.length === 0 ? undefined : trimmedLabel.toLowerCase().replace(/[\s]+/g, '_');
  }
  return undefined;
};

class Tracking {
  public static instance: Tracking;
  private _wbMediaQueryService = new MediaQueryService(window);
  private _appIdentifier: ApplicationIdentifier;
  private _retailerId: string;
  public static instancePromise: Promise<TrackingInstance>;
  public static instanceResolver: (value: TrackingInstance) => void;

  constructor(appId: ApplicationIdentifier, retailerId: string) {
    this._appIdentifier = appId;
    this._retailerId = retailerId;
  }

  private _isInstanceOfSpaEvent(eventObject: Event | SpaEvent): eventObject is SpaEvent {
    return '_isLinkInSpa' in eventObject;
  }

  private _pushToGlobalLinkTracker(targetElement: HTMLElement) {
    const anchorElement = targetElement as HTMLAnchorElement;
    const trackingEventObject = this._createClickEventObjectToPush({
      action: this.getAction(anchorElement),
      label: anchorElement.innerText,
      URL: anchorElement.href,
    });

    this._pushToTrackingObject(trackingEventObject);
  }

  /**
   * Turns every empty field in the pageview into undefined as
   * requested by the analytics team
   *
   * @param trackingFields - the page object
   * @returns a copy of the same page object with '' mapped to undefined
   */
  private _processPageViewValuesIfEmpty(trackingFields: TrackingDetailObject): TrackingDetailObject {
    const trackingFieldsCopy = { ...trackingFields };

    Object.keys(trackingFieldsCopy).forEach((field: string) => {
      if (trackingFieldsCopy[field] === '') {
        trackingFieldsCopy[field] = undefined;
      } else if (trackingFieldsCopy[field] instanceof Object) {
        trackingFieldsCopy[field] = this._processPageViewValuesIfEmpty(trackingFieldsCopy[field]);
      }
    });

    return trackingFieldsCopy;
  }
  /**
   *  Is Triggered when there is a click on the document and checks if the clicked target
   * is a link or if it as the class necessary to be tracked
   *
   * Links are tracked by default (if we use the class 'non-trackable' the default tracking
   * is cancelled)
   *
   * Can also collect and track elements with the class 'trackable'
   *
   * After using the necessary methods to create the desired object structure it calls
   * _pushToTrackingObject that handles the analytics object push
   *
   * @param {event} evt the click event from the listener
   */
  //
  private globalLinkTracker(evt: Event | SpaEvent) {
    /* Checks helper boolean to see if the element clicked comes from an SPA.
     * If so we discard it.
     */
    if (this._isInstanceOfSpaEvent(evt) && evt._isLinkInSpa) {
      return;
    }

    // composedPath()[0] instead of target because there is a possibility that the elements
    // clicked are inside a shadow Root
    const clickTarget = evt.composedPath()[0];
    if (!(clickTarget instanceof HTMLElement || SVGElement)) return;
    const targetElement = clickTarget as HTMLElement;

    if (
      !(targetElement.getAttribute(TRACKING_ATTRIBUTES.trackingTrigger) === 'false') &&
      targetElement.tagName === VALUES.anchorTag
    ) {
      this._pushToGlobalLinkTracker(targetElement);
    }
    // sometimes there is a need to track other elements and for these cases we can add
    // the attribute "trackable". If the clicked element contains this attribute we have
    // to check the tracking data attributes in the dataset and pass them to the tracking
    // object.
    // This is for the simple cases where we want to track a click in something that is not
    // a link. For more complex cases please use _customTrackingObjectUpdate
    else if (targetElement.getAttribute(TRACKING_ATTRIBUTES.trackingTrigger) === 'true') {
      const trackingObject = targetElement.dataset.owcTrackingObject;
      if (!trackingObject) return;
      const trackingEventObject = this._createClickEventObjectToPush(JSON.parse(trackingObject));
      this._pushToTrackingObject(trackingEventObject);
    } else if (targetElement.tagName !== VALUES.anchorTag) {
      const wrapperAnchor = targetElement.closest('a');

      if (wrapperAnchor && wrapperAnchor.getAttribute(TRACKING_ATTRIBUTES.trackingTrigger) !== 'false') {
        this._pushToGlobalLinkTracker(wrapperAnchor);
      }
    }
  }

  /**
   *
   * Method used to fetch the element action to pass in the tracking object.
   *
   * If there is no action defined the analytics colleagues want the value to be
   * undefined
   *
   * @param {string} trackingElement  element being tracked
   */
  private getAction(trackingElement: HTMLElement | undefined): string | undefined {
    const parentElement = trackingElement?.closest(`[${TRACKING_DATA_ATTRIBUTES.componentAction}]`);
    const action = parentElement?.getAttribute(TRACKING_DATA_ATTRIBUTES.componentAction);

    if (!action) {
      return;
    }
    return action;
  }

  /**
   *  Receives an Object with the desired field to push and constructs the default click event
   * object
   * @param {object} trackingFields  Fields that will be used in the eventDetail
   */
  private _createClickEventObjectToPush(trackingFields: object): ClickEventObject {
    trackingFields = this._trimAndFormat(trackingFields);

    return {
      event: EventType.click,
      eventDetail: {
        category: 'link',
        retailerId: this._retailerId,
        ...trackingFields,
      },
      application: this._appIdentifier,
    };
  }

  /**
   *  Receives an Object with the desired field to push and constructs the default pageview event
   * object
   * @param {object} trackingFields  Fields that will be used in the eventDetail
   */
  private _createPageViewEventObjectToPush(trackingFields: TrackingDetailObject): PageViewEventObject {
    trackingFields = this._processPageViewValuesIfEmpty(trackingFields);

    return {
      event: EventType.pageView,
      page: {
        ...trackingFields,
      },
      application: this._appIdentifier,
    };
  }

  /**
   *  Receives an Object with the desired field to push and constructs the default global event
   * object
   * @param {GlobalData} globalEventPayload  Fields that will be used in the global event
   * @param {string} loginState  contains the status of the login
   * @param {string} currentMQ  contains the current MQ of the viewport
   * @param {Plugin[]} plugins  contains all the plugins that are in the page
   */
  //
  private _createGlobalEventObjectToPush(
    globalEventPayload: GlobalData,
    loginState: string,
    currentMQ: string,
    plugins: Plugin[],
  ): GlobalEventObject {
    return {
      event: EventType.global,
      site: {
        market: globalEventPayload.market,
        language: globalEventPayload.language,
        currency: globalEventPayload.currency,
        mediaQuery: currentMQ,
        sysEnv: globalEventPayload.sysEnv,
      },
      user: {
        loginState: loginState,
      },
      plugin: plugins,
      application: this._appIdentifier,
    };
  }

  private _pushToTrackingObject(payload: object) {
    if (window.dataLayer_ow) {
      window.dataLayer_ow.push(payload);
    }
    // TODO: remove this
    else {
      window.dataLayer_ow = []; // eslint-disable-line
      window.dataLayer_ow.push(payload);
    }
  }

  /**
   * Fetches the currentMQ from the workbench media query service.
   *
   * Used for the creation of the Global event.
   */
  private _getCurrentMQ(): string {
    return this._wbMediaQueryService.getCurrentMediaQuery();
  }

  /**
   * Changes the boolean on the analytics helper to ensure that whenever a link is clicked inside an
   * SPA the global link tracker knows that it should not be tracked
   */
  private _preventSpaTrackingHelper(spaEvent: Event): void {
    (spaEvent as SpaEvent)._isLinkInSpa = true;
  }

  /**
   * Selects all the plugins that are inserted in the page and formats the array
   * to push it alongside the Global event.
   *
   * The queryselector catches all 3 types of SPA integrations and formats the final
   * object accordingly. These types are:
   *
   * Iframe => Thankfully even thought we have iFrames on the page which aren't SPAs,
   * the ones that are, always have the plugincomponent__base class
   *
   * WebComponent => These are easy to catch since all webComponents have the webComponent
   * class (AEM shenanigans). The only thing to ensure is that we filter out our own components
   * which are always prefixed with a 'OWC-' (the dash is necessary due to components like OWCC)
   *
   * InDom => Every InDom integration needs the app-instance-id attribute so that the SPA knows
   * where to bootstrap itself. Even though the iFrames also have this attribute internally, document
   * query selector will never query inside the iframe itself so this is not an issue.
   *
   * In addition all three cases get an event listener that intercepts click events and
   * gives them a property that we can check later to discard these clicks from being tracked
   * by us. (they are tracked internally inside their on apps)
   */
  private _getPagePlugins(): Plugin[] {
    return Array.from(
      document.querySelectorAll(
        // To deprecate .webcomponent:not(div) when content no longer has legacy Seamless Web Components
        '.webcomponent:not(div), [data-integration-type], [app-instance-id], iframe.plugincomponent__base',
      ),
    )
      .filter(possiblePlugin => !possiblePlugin.tagName.startsWith('OWC-'))
      .map(plugin => {
        let pluginId: string;
        let pluginIntegrationType: IntegrationType;

        if (plugin.tagName === 'IFRAME') {
          pluginId = plugin.id;
          pluginIntegrationType = IntegrationType.iframe;
        } else if (plugin.hasAttribute('app-instance-id')) {
          pluginId = plugin.tagName;
          pluginIntegrationType = IntegrationType.inDom;
        } else if (plugin.hasAttribute('data-integration-type')) {
          pluginId = plugin.getAttribute('data-component-name') || plugin.tagName;
          pluginIntegrationType = IntegrationType.vueComponent;
        } else {
          pluginId = plugin.tagName.toLowerCase();
          pluginIntegrationType = IntegrationType.webComponent;
        }

        // add special event listener to ensure that links clicked in spa's are not tracked
        plugin.addEventListener('click', clickEvent => this._preventSpaTrackingHelper(clickEvent));

        return {
          id: pluginId,
          type: pluginIntegrationType,
        };
      });
  }

  /**
   * Fetches the loginState from the session storage.
   *
   * Used for the creation of the Global event.
   */
  private _getLoginState(): string {
    return window.sessionStorage.getItem(VALUES.sessionStorageLoginState) || VALUES.loggedOut;
  }

  /**
   * Formats the payload label according
   * to the analytics team specifications.
   * Every white space should be replaced with
   * a '_'
   *
   * @param payload: Any kind of object that contains at least one label
   */
  private _trimAndFormat(payload: Record<string, any>) {
    if (!payload.label) {
      return payload;
    }

    payload.label = formatTrackingString(payload.label);

    return payload;
  }

  /**
   *
   * Method used when we want to control the tracking via the component itself.
   *
   * Used in special cases where the tracking does not follow the standard rules.
   *
   * The components can use it for different types of events (with pre configured
   * object field) or with a custom eventType were the object that will be pushed is
   * fully customizable.
   *
   * @param {object} eventDetailPayload  Payload to be pushed to the tracking object
   * @param {string} eventType  Event type
   * @param {Element} trackingElement  element used to fetch the correct action
   *
   */
  public customTrackingObjectUpdate(
    eventDetailPayload: EventDetailPayload,
    eventType: string,
    trackingElement: Element | undefined,
  ) {
    if (trackingElement && !eventDetailPayload.action) {
      eventDetailPayload = {
        ...eventDetailPayload,
        action: this.getAction(trackingElement as HTMLElement),
      };
    }
    switch (eventType) {
      case EventType.click:
        this._pushToTrackingObject(this._createClickEventObjectToPush(eventDetailPayload));
        break;
      case EventType.pageView:
        this._pushToTrackingObject(this._createPageViewEventObjectToPush(eventDetailPayload));
        break;
      case EventType.custom:
        this._pushToTrackingObject(eventDetailPayload);
        break;
    }
  }

  /**
   *
   * Gathers all the parameters necessary to construct the Global event that is pushed
   * when the component loads.
   *
   * After using the necessary methods to create the desired object structure it calls
   * _pushtoTrackingObject that handles the analytics object push
   *
   * @param {GlobalData} globalEventPayload  Fields that will be used in the global event
   */
  //
  public triggerGlobalEvent(globalEventPayload: GlobalData) {
    const loginState = this._getLoginState(),
      currentMQ = this._getCurrentMQ(),
      plugins = this._getPagePlugins();

    this._pushToTrackingObject(
      this._createGlobalEventObjectToPush(globalEventPayload, loginState, currentMQ, plugins),
    );
  }

  /**
   *
   * Gathers all the parameters necessary to construct the pageview event that is pushed
   * when the component loads.
   *
   * After using the necessary methods to create the desired object structure it calls
   * _pushToTrackingObject that handles the analytics object push
   *
   * @param {PageData} pageViewPayload  Fields that will be used in the global event
   */
  //
  public triggerPageViewEvent(pageViewPayload: PageData) {
    this._pushToTrackingObject(this._createPageViewEventObjectToPush(pageViewPayload));
  }

  public triggerLinkTracker() {
    document.addEventListener('click', evt => {
      this.globalLinkTracker(evt);
    });
  }
}

/**
 *
 * Helper method for the initializing the tracking instance
 *
 * @param {TrackingPayload} initialTrackingPayload  datajson that is passed to the componet
 * from the BE
 */
//

function setTrackingPayload(initialTrackingPayload: TrackingPayload) {
  Tracking.instance = new Tracking(
    initialTrackingPayload.appIdentifier,
    initialTrackingPayload.page.retailerId,
  );

  Tracking.instance.triggerLinkTracker();
  Tracking.instance.triggerGlobalEvent(initialTrackingPayload.global);
  Tracking.instance.triggerPageViewEvent(initialTrackingPayload.page);

  Tracking.instanceResolver(Tracking.instance);
}

/**
 *
 * Method called when the tracking component is mounted on the page.
 *
 * It triggers the global and pageview object pushes and set the appIdentifier.
 *
 * @param {TrackingPayload} initialTrackingPayload  datajson that is passed to the componet
 * from the BE
 */
//
export default function initializeTracking(
  initialTrackingPayload?: TrackingPayload,
): Promise<TrackingInstance> {
  if (!Tracking.instancePromise) {
    Tracking.instancePromise = new Promise(resolve => {
      Tracking.instanceResolver = resolve;
    });
  }

  if (initialTrackingPayload) {
    setTrackingPayload(initialTrackingPayload);
  }
  return Tracking.instancePromise;
}
